import { addDays, compareLocalDates, differenceInCalendarDays } from './dates';
import { formatMoney } from './money';
import type { NotificationCandidate, NotificationInput } from './types';

export function generateNotifications(input: NotificationInput): NotificationCandidate[] {
  const candidates: NotificationCandidate[] = [];
  for (const event of input.plannedEvents ?? []) {
    if (event.status === 'SETTLED' || event.status === 'CANCELLED' || event.status === 'SKIPPED')
      continue;
    const overdue = event.status === 'OVERDUE';
    const withinSevenDays =
      compareLocalDates(event.dueWindowStart, input.asOfDate) >= 0 &&
      compareLocalDates(event.dueWindowStart, addDays(input.asOfDate, 7)) <= 0;
    if (overdue) {
      const income = event.type === 'INCOME';
      candidates.push({
        dedupeKey: `plan:${event.id}:${income ? 'missed-income' : 'overdue-bill'}:${event.dueWindowEnd}`,
        kind: income ? 'EXPECTED_INCOME_MISSED' : 'OVERDUE_BILL',
        severity: income ? 'WARNING' : 'CRITICAL',
        subjectId: event.id,
        effectiveDate: event.dueWindowEnd,
        finding: income ? `${event.label} has not been received.` : `${event.label} is overdue.`,
        evidence: `${formatMoney(event.remainingAmount)} remains unsettled after ${event.dueWindowEnd}.`,
        possibleAction: income
          ? 'Confirm a new expected date or record the income when received.'
          : 'Pay, partially settle, reschedule, or explicitly cancel the obligation.',
      });
    } else if (withinSevenDays) {
      const income = event.type === 'INCOME';
      candidates.push({
        dedupeKey: `plan:${event.id}:${income ? 'upcoming-income' : 'upcoming-bill'}:${event.dueWindowStart}`,
        kind: income ? 'UPCOMING_INCOME' : 'UPCOMING_BILL',
        severity: 'INFO',
        subjectId: event.id,
        effectiveDate: event.dueWindowStart,
        finding: `${event.label} is expected soon.`,
        evidence: `${formatMoney(event.remainingAmount)} is scheduled from ${event.dueWindowStart}.`,
        possibleAction: income
          ? 'Record it only after the money arrives.'
          : 'Confirm the funding account before the due window.',
      });
    }
  }

  if (input.forecast?.minimumUnprotectedLiquidBalance !== undefined) {
    if (input.forecast.minimumUnprotectedLiquidBalance < 0) {
      candidates.push({
        dedupeKey: `forecast:shortfall:${input.forecast.minimumUnprotectedLiquidDate}`,
        kind: 'CASH_SHORTFALL',
        severity: 'CRITICAL',
        subjectId: 'forecast',
        effectiveDate: input.forecast.minimumUnprotectedLiquidDate,
        finding: 'The current plan contains a projected cash shortfall.',
        evidence: `Unprotected liquid cash reaches ${formatMoney(input.forecast.minimumUnprotectedLiquidBalance)}.`,
        possibleAction:
          'Move an eligible discretionary commitment or preserve more cash before the low point.',
      });
    } else if (input.forecast.safetyBufferBreach) {
      candidates.push({
        dedupeKey: `forecast:buffer:${input.forecast.minimumDate}`,
        kind: 'SAFETY_BUFFER',
        severity: 'WARNING',
        subjectId: 'forecast',
        effectiveDate: input.forecast.minimumDate,
        finding: 'Projected cash falls below the configured safety buffer.',
        evidence: `Accessible headroom reaches ${formatMoney(input.forecast.minimumAccessibleBalance)}.`,
        possibleAction: 'Review discretionary spending before the projected low point.',
      });
    }
  }

  for (const debt of input.debts ?? []) {
    if (
      !debt.dueDate ||
      debt.status === 'SETTLED' ||
      debt.status === 'CANCELLED' ||
      debt.status === 'WRITTEN_OFF'
    )
      continue;
    const overdue = debt.status === 'OVERDUE';
    const upcoming =
      compareLocalDates(debt.dueDate, input.asOfDate) >= 0 &&
      compareLocalDates(debt.dueDate, addDays(input.asOfDate, 7)) <= 0;
    if (!overdue && !upcoming) continue;
    const receivable = debt.direction === 'RECEIVABLE';
    candidates.push({
      dedupeKey: `debt:${debt.id}:${overdue ? 'overdue' : 'due'}:${debt.dueDate}`,
      kind: receivable
        ? overdue
          ? 'RECEIVABLE_OVERDUE'
          : 'RECEIVABLE_DUE'
        : overdue
          ? 'DEBT_OVERDUE'
          : 'DEBT_DUE',
      severity: overdue ? 'WARNING' : 'INFO',
      subjectId: debt.id,
      effectiveDate: debt.dueDate,
      finding: `${debt.counterparty} ${receivable ? 'receivable' : 'payable'} is ${overdue ? 'overdue' : 'due soon'}.`,
      evidence: `${formatMoney(debt.remainingAmount)} remains outstanding.`,
      possibleAction: receivable
        ? 'Follow up or record any amount received.'
        : 'Confirm the repayment funding account.',
    });
  }

  for (const goal of input.goals ?? []) {
    if (goal.feasible || goal.contributionGap <= 0) continue;
    candidates.push({
      dedupeKey: `goal:${goal.id}:behind:${goal.targetDate ?? 'no-date'}`,
      kind: 'GOAL_BEHIND',
      severity: 'WARNING',
      subjectId: goal.id,
      effectiveDate: goal.targetDate ?? input.asOfDate,
      finding: `${goal.name} is behind its current contribution plan.`,
      evidence: `The calculated monthly contribution gap is ${formatMoney(goal.contributionGap)}.`,
      possibleAction:
        'Adjust the contribution, target amount, or target date and rerun feasibility.',
    });
  }

  const reminderDays = input.backupReminderDays ?? 30;
  if (
    !input.lastBackupDate ||
    differenceInCalendarDays(input.lastBackupDate, input.asOfDate) >= reminderDays
  ) {
    candidates.push({
      dedupeKey: `backup:${input.asOfDate.slice(0, 7)}`,
      kind: 'BACKUP_REMINDER',
      severity: 'INFO',
      subjectId: 'backup',
      effectiveDate: input.asOfDate,
      finding: 'A fresh off-site backup is due.',
      evidence: input.lastBackupDate
        ? `The last recorded backup was ${input.lastBackupDate}.`
        : 'No completed backup is recorded.',
      possibleAction:
        'Export the versioned JSON backup and store it in a trusted private location.',
    });
  }

  return [
    ...new Map(candidates.map((candidate) => [candidate.dedupeKey, candidate])).values(),
  ].sort(
    (left, right) =>
      left.effectiveDate.localeCompare(right.effectiveDate) ||
      left.dedupeKey.localeCompare(right.dedupeKey),
  );
}
